/**
 * VÉRAULT Portal — Treasury (Wave 36 · Phase 1 · PI-8)
 *
 * Calm firm-level financial snapshot. Read-only today; reads from the
 * new /api/treasury/summary + /outstanding endpoints. Purposely
 * minimal — the heavier per-trip payments UI lives inside each
 * Journey detail (Payments tab).
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Wallet, AlertTriangle, Clock, Loader2 } from "lucide-react";
import {
  Surface,
  StatusTag,
  PageHeader,
  EditorialRule,
  AIWhisper,
  MetricTile,
  PortalBreadcrumb,
  api,
  motion as portalMotion,
} from "../../index";

const { fadeInUp, stagger } = portalMotion;

function fmtCurrency(amount, currency = "USD") {
  if (amount === null || amount === undefined) return "—";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency", currency: currency || "USD", maximumFractionDigits: 0,
    }).format(Number(amount));
  } catch {
    return `${currency} ${amount}`;
  }
}

function fmtDate(value) {
  if (!value) return null;
  try {
    return new Date(value).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return String(value);
  }
}

function OutstandingRow({ row }) {
  const overdue = row.overdue_count > 0;
  return (
    <Link
      to={`/portal/advisor/journeys/${row.trip_id}?tab=payments`}
      className="block rounded border border-[hsl(var(--surface-hairline))] bg-[hsl(var(--background-paper))] hover:border-[hsl(var(--accent-gold))]/40 transition-colors p-3"
      data-testid={`treasury-row-${row.trip_id}`}
    >
      <div className="flex items-start gap-3 flex-wrap">
        <span className={`inline-flex items-center justify-center size-9 rounded-full shrink-0 ${
          overdue ? "bg-[hsl(var(--accent-terracotta))]/15 text-[hsl(var(--accent-terracotta))]" :
                    "bg-[hsl(var(--background-stone))] text-[hsl(var(--foreground-muted))]"
        }`}>
          {overdue ? <AlertTriangle className="size-4" strokeWidth={1.5} /> : <Wallet className="size-4" strokeWidth={1.5} />}
        </span>
        <div className="flex-1 min-w-0">
          {row.experience_code && (
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--accent-gold))]">
              {row.experience_code}
            </p>
          )}
          <p className="font-serif text-[15px] mt-0.5">{row.title}</p>
          <p className="font-mono text-[11px] text-[hsl(var(--foreground-muted))] mt-0.5">
            {row.client_name || "—"}{row.trip_start_date ? ` · ${fmtDate(row.trip_start_date)}` : ""}
          </p>
        </div>
        <div className="text-right shrink-0">
          <p className="font-serif text-[16px]">{fmtCurrency(row.outstanding, row.currency)}</p>
          <div className="mt-1 flex items-center justify-end gap-1.5">
            {overdue && <StatusTag tone="warning">{row.overdue_count} overdue</StatusTag>}
            {row.next_due_at && !overdue && (
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">
                <Clock className="size-3 inline mr-1" /> due {fmtDate(row.next_due_at)}
              </span>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
}

export default function Treasury() {
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [s, o] = await Promise.all([
          api.get("/treasury/summary").then((r) => r.data).catch(() => null),
          api.get("/treasury/outstanding?limit=100").then((r) => r.data?.rows || []).catch(() => []),
        ]);
        if (!alive) return;
        setSummary(s);
        setRows(o);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  return (
    <motion.div variants={stagger} initial="initial" animate="animate" className="space-y-6" data-testid="treasury-page">
      <PortalBreadcrumb
        parent={{ to: "/portal/advisor", label: "Atelier" }}
        trail={[{ label: "Treasury" }]}
      />
      <PageHeader
        eyebrow="VÉRAULT · Treasury"
        title="Treasury"
        lede="A quiet snapshot of what's outstanding · what's overdue · what's due soon."
      />

      <motion.div variants={fadeInUp}>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <MetricTile
            label="Trips with balance"
            value={summary?.trips_with_balance ?? 0}
            format="number"
            icon={Wallet}
          />
          <MetricTile
            label="Outstanding"
            value={summary?.total_outstanding ?? 0}
            format="money"
            icon={Wallet}
          />
          <MetricTile
            label="Overdue"
            value={summary?.overdue_amount ?? 0}
            format="money"
            icon={AlertTriangle}
          />
          <MetricTile
            label="Due in 7 days"
            value={summary?.due_soon_amount ?? 0}
            format="money"
            icon={Clock}
          />
        </div>
      </motion.div>

      {summary?.overdue_count > 0 && (
        <motion.div variants={fadeInUp}>
          <Surface variant="paper" padded className="!p-4 border-[hsl(var(--accent-terracotta))]/40">
            <div className="flex items-start gap-3">
              <AlertTriangle className="size-4 text-[hsl(var(--accent-terracotta))] mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--accent-terracotta))]">
                  Overdue
                </p>
                <p className="font-serif text-[16px] mt-0.5">
                  {summary.overdue_count} payment{summary.overdue_count === 1 ? "" : "s"} ·{" "}
                  {fmtCurrency(summary.overdue_amount, Object.keys(summary.currencies || {})[0] || "USD")}
                </p>
                <p className="font-accent italic text-[12.5px] text-[hsl(var(--foreground-muted))] mt-0.5">
                  Listed first below. Click any trip to open its Payments tab.
                </p>
              </div>
            </div>
          </Surface>
        </motion.div>
      )}

      <motion.div variants={fadeInUp} className="space-y-2">
        <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
          Outstanding · {rows.length}
        </p>
        {loading ? (
          <Surface variant="paper" padded>
            <p className="font-accent italic text-[13px] text-[hsl(var(--foreground-muted))] flex items-center gap-2">
              <Loader2 className="size-3.5 animate-spin" /> Loading…
            </p>
          </Surface>
        ) : rows.length === 0 ? (
          <Surface variant="paper" padded>
            <p className="font-accent italic text-[13px] text-[hsl(var(--foreground-muted))]">
              Nothing outstanding. Everything is paid up.
            </p>
          </Surface>
        ) : (
          rows.map((r) => <OutstandingRow key={r.trip_id} row={r} />)
        )}
      </motion.div>

      <EditorialRule />
      <AIWhisper label="A quiet note">
        Treasury reads from each trip&apos;s payment ledger. To collect a payment, open the trip and use the Payments tab.
      </AIWhisper>
    </motion.div>
  );
}
